import React, { useMemo, useState } from "react";
import { Badge, Card, Heading, Icon, Tabs, Text } from "@telia/teddy";
import cn from "classnames";
import _isEmpty from "lodash/isEmpty";
import { Item } from "../../../state/selectors";
import "./SummaryTable.less";

const TABS = [
    { id: "FWA_FIRST_12", name: "Første 12 md." },
    { id: "FWA_AFTER_12", name: "Etter 12 md." },
];

const UpfrontItems: React.FC<{ flashText?: string; upfrontItems: Item[]; showBorder?: boolean; heading?: string }> = ({
    flashText,
    upfrontItems,
    showBorder,
    heading,
}) => (
    <div className={cn("fwa-summary__table", { "fwa-summary__table--with-border": showBorder })}>
        {flashText && <div className="fwa-summary__flashtext">{flashText}</div>}
        {heading && (
            <Heading mb="100" className="fwa-summary__table__item__heading" as="h2" variant="subsection-100">
                {heading}
            </Heading>
        )}
        {upfrontItems.map((item, index) => (
            <div key={`table-item-${item.key}-${index}`} className="fwa-summary__table-item">
                <Text variant="paragraph-100" className="fwa-summary__table-item-key">
                    {item.key}
                </Text>
                <div className="fwa-summary__table-item-value">
                    <Text variant="paragraph-100">{item.value}</Text>
                    {item.strikethrough && (
                        <Text variant="paragraph-100" className="fwa-summary__table-item-strikethrough">
                            {item.strikethrough}
                        </Text>
                    )}
                </div>
            </div>
        ))}
    </div>
);

const MonthlyItems: React.FC<{
    flashText?: string;
    monthlyItems: Item[];
    showBorder?: boolean;
    heading?: string;
    showDiscount?: boolean;
}> = ({ flashText, monthlyItems, showBorder, heading, showDiscount }) => (
    <div className={cn("fwa-summary__table", { "fwa-summary__table--with-border": showBorder })}>
        {flashText && <div className="fwa-summary__flashtext">{flashText}</div>}
        {heading && (
            <Heading mb="100" className="fwa-summary__table__item__heading" as="h2" variant="subsection-100">
                {heading}
            </Heading>
        )}
        {monthlyItems.map((item, index) => (
            <div key={`table-item-${item.key}-${index}`} className="fwa-summary__table-item">
                <Text variant="paragraph-100" className="fwa-summary__table-item-key">
                    {item.key}
                    {item.key.toLowerCase().includes("smart wifi router") && <span>Inkluderer 12 md. bindingstid</span>}
                </Text>
                <div className="fwa-summary__table-item-value">
                    <Text variant="paragraph-100">{showDiscount ? item.value : item.strikethrough || item.value}</Text>
                    {showDiscount && item.strikethrough && (
                        <Text variant="paragraph-100" className="fwa-summary__table-item-strikethrough">
                            {item.strikethrough}
                        </Text>
                    )}
                </div>
            </div>
        ))}
    </div>
);

const TotalPrice: React.FC<{
    totalPrice?: number;
    totalDiscountedPrice?: number;
    showDiscounted?: boolean;
    totalUpfrontPrice?: number;
    totalUpfrontDiscountedPrice?: number;
}> = ({ totalPrice, totalDiscountedPrice, totalUpfrontPrice, totalUpfrontDiscountedPrice, showDiscounted }) => {
    const totalUpfrontDiscountedPriceDisplay = `${totalUpfrontDiscountedPrice ?? totalUpfrontPrice ?? 0},-`;
    const totalPriceYearly = `${(totalPrice ?? 0) * 12},-`;
    const totalDiscountedPriceYearly = `${(totalDiscountedPrice ?? totalPrice ?? 0) * 12},-`;
    const totalPriceMonthly = `${totalPrice ?? 0},-/md.`;
    const totalDiscountedPriceMonthly = `${totalDiscountedPrice ?? totalPrice ?? 0},-/md.`;
    const savingsFirstYear =
        totalPrice !== undefined && totalDiscountedPrice !== undefined
            ? totalPrice * 12 - totalDiscountedPrice * 12
            : 0;

    return (
        <div className="fwa-summary__price-table">
            <Card className="fwa-summary__price-table__container" variant="gray">
                <div className="fwa-summary__flashtext fwa-summary__flashtext__timeperiod">
                    {showDiscounted ? "Første 12 md." : "Etter 12 md."}
                </div>
                {savingsFirstYear > 0 && (
                    <Badge className="fwa-summary__price-table-item__ribbon" hideIcon variant="success">
                        <Icon className="fwa-summary__price-table-item__ribbon--icon" name="tag" size="md" />
                        <Text>Du sparer {savingsFirstYear},- første året!</Text>
                    </Badge>
                )}
                <div className="fwa-summary__price-table-item">
                    <Text variant="paragraph-100" className="fwa-summary__table-item-key">
                        Per måned
                        <span>
                            Totalt etter 12 md. {showDiscounted ? totalDiscountedPriceYearly : totalPriceYearly}
                        </span>
                    </Text>
                    <div className="fwa-summary__table-item-value">
                        <Text className="fwa-summary__table-item-value__monthly" variant="paragraph-100">
                            {showDiscounted ? totalDiscountedPriceMonthly : totalPriceMonthly}
                        </Text>
                    </div>
                </div>
                {showDiscounted && totalUpfrontDiscountedPrice !== undefined && totalUpfrontDiscountedPrice > 0 && (
                    <div className="fwa-summary__price-table-item">
                        <Text variant="paragraph-100" className="fwa-summary__table-item-key">
                            Engangskostnad
                        </Text>
                        <div className="fwa-summary__table-item-value">
                            <Text variant="paragraph-100">{totalUpfrontDiscountedPriceDisplay}</Text>
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
};

interface SummaryTableProps {
    flashText?: string;
    items: Item[];
    upfrontItems: Item[];
    totalUpfrontPrice?: number;
    totalUpfrontDiscountedPrice?: number;
    showBorder?: boolean;
    showTabs?: boolean;
    priceTitle?: string;
    upfrontPriceTitle?: string;
    totalDiscountedPrice?: number;
    totalPrice?: number;
}

const SummaryTable: React.FC<SummaryTableProps> = ({
    items,
    upfrontItems,
    flashText,
    totalPrice,
    totalDiscountedPrice,
    totalUpfrontPrice,
    totalUpfrontDiscountedPrice,
    showBorder = true,
    showTabs = true,
}) => {
    const shouldShowTabs = useMemo(() => {
        return showTabs && items.some((item) => !!item.strikethrough);
    }, [items, showTabs]);
    const [selectedTabsId, setSelectedTabsId] = useState("FWA_FIRST_12");
    const showDiscounted = selectedTabsId === "FWA_FIRST_12";

    return (
        <>
            {shouldShowTabs && (
                <Tabs
                    className="fwa-summary__table-tabs"
                    fullWidth
                    value={selectedTabsId}
                    onValueChange={(id) => setSelectedTabsId(id)}
                >
                    <Tabs.List>
                        {TABS.map((tab) => (
                            <Tabs.Trigger value={tab.id} key={tab.id}>
                                {tab.name}
                            </Tabs.Trigger>
                        ))}
                    </Tabs.List>
                </Tabs>
            )}
            {!_isEmpty(upfrontItems) && (
                <UpfrontItems
                    flashText={flashText}
                    upfrontItems={upfrontItems}
                    showBorder={showBorder}
                    heading="INSTALLASJON"
                />
            )}
            {!_isEmpty(items) && (
                <MonthlyItems
                    flashText={flashText}
                    monthlyItems={items}
                    showBorder={showBorder}
                    heading="DITT ABONNEMENT"
                    showDiscount={showDiscounted}
                />
            )}
            <TotalPrice
                totalPrice={totalPrice}
                totalDiscountedPrice={totalDiscountedPrice}
                totalUpfrontPrice={totalUpfrontPrice}
                totalUpfrontDiscountedPrice={totalUpfrontDiscountedPrice}
                showDiscounted={showDiscounted}
            />
        </>
    );
};

export default SummaryTable;
